import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import TickerWithName from "../components/TickerWithName";
import { QuickTradeEquityButton } from "../components/QuickTradeButtons";

// ---- Chart.js (line + fallback candles) ----
import {
    Chart as ChartJS,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    TimeScale,
    Title,
    Tooltip,
    Legend,
    CategoryScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Chart as ReactChart } from "react-chartjs-2";
import {
    CandlestickController,
    CandlestickElement,
    OhlcController,
    OhlcElement,
} from "chartjs-chart-financial";

ChartJS.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    TimeScale,
    Title,
    Tooltip,
    Legend,
    CategoryScale,
    CandlestickController,
    OhlcController,
    CandlestickElement,
    OhlcElement
);

// ---------- UI options ----------
const RANGE_OPTIONS = [
    { key: "1d", label: "1D" },
    { key: "5d", label: "5D" },
    { key: "1mo", label: "1M" },
    { key: "3mo", label: "3M" },
    { key: "6mo", label: "6M" },
    { key: "1y", label: "1Y" },
    { key: "2y", label: "2Y" },
    { key: "5y", label: "5Y" },
    { key: "max", label: "Max" },
];

const INTERVAL_OPTIONS = [
    "1m",
    "2m",
    "5m",
    "15m",
    "30m",
    "60m",
    "90m",
    "1h",
    "2h",
    "4h",
    "1d",
    "1wk",
    "1mo",
];

// ---------- helpers ----------
function normalizeInterval(iv) {
    if (iv === "1h") return "60m";
    if (iv === "2h") return "120m";
    if (iv === "4h") return "240m";
    return iv;
}
function normalizeRange(r) {
    return r;
}
function sma(arr, period) {
    const out = new Array(arr.length).fill(null);
    let sum = 0;
    for (let i = 0; i < arr.length; i++) {
        const v = Number(arr[i] ?? 0);
        sum += v;
        if (i >= period) sum -= Number(arr[i - period] ?? 0);
        if (i >= period - 1) out[i] = +(sum / period).toFixed(4);
    }
    return out;
}

const ChartPage = () => {
    const { ticker } = useParams();
    const navigate = useNavigate();

    const [range, setRange] = useState("1mo");
    const [interval, setInterval] = useState("1h");
    const [mode, setMode] = useState("candles"); // "line" | "candles"

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // series state
    const [candles, setCandles] = useState([]);
    const [volumes, setVolumes] = useState([]);
    const [lineData, setLineData] = useState(null);
    const [lineOptions, setLineOptions] = useState(null);

    // LWC refs
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const candleSeriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);
    const resizeObsRef = useRef(null);

    // fallback (Chart.js) candlesticks
    const [useFallback, setUseFallback] = useState(false);
    const [fallbackCandleData, setFallbackCandleData] = useState(null);
    const [fallbackCandleOptions, setFallbackCandleOptions] = useState(null);

    // network control
    const abortRef = useRef(null);
    const debounceRef = useRef(null);

    // ---------- Fetch price data when ticker/range/interval change ----------
    useEffect(() => {
        // debounce rapid clicks
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            // cancel any in-flight request
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = new AbortController();

            try {
                setLoading(true);
                setErr("");

                const rangeNorm = normalizeRange(range);
                const intervalNorm = normalizeInterval(interval);

                const { data } = await axios.get(
                    "https://mystockpicks.onrender.com/api/price-chart",
                    {
                        params: {
                            ticker,
                            range: rangeNorm,
                            interval: intervalNorm,
                            _: Date.now(), // cache-buster
                        },
                        timeout: 25000,
                        signal: abortRef.current.signal,
                    }
                );

                // handle empty/no data
                if (!data || !data.timestamp) {
                    setCandles([]);
                    setVolumes([]);
                    setLineData(null);
                    return;
                }

                const timestamps = data.timestamp || [];
                const q = data.indicators?.quote?.[0] || {};
                const closes = q.close || [];
                const opens = q.open || closes;
                const highs = q.high || closes;
                const lows = q.low || closes;
                const vols = q.volume || [];

                // compact valid points
                const pts = timestamps
                    .map((ts, i) => ({
                        ts,
                        c: closes[i],
                        o: opens[i],
                        h: highs[i],
                        l: lows[i],
                        v: vols[i],
                    }))
                    .filter(
                        (p) =>
                            p.ts &&
                            p.c != null &&
                            p.o != null &&
                            p.h != null &&
                            p.l != null
                    );

                // build candles + volumes
                const cdl = pts.map((p) => ({
                    time: p.ts,
                    open: +p.o,
                    high: +p.h,
                    low: +p.l,
                    close: +p.c,
                }));
                const vol = pts.map((p) => ({
                    time: p.ts,
                    value: Math.max(0, +p.v || 0),
                    color:
                        p.c >= p.o
                            ? "rgba(34,197,94,0.6)"
                            : "rgba(239,68,68,0.6)",
                }));
                setCandles(cdl);
                setVolumes(vol);

                // line chart + SMAs
                const x = pts.map((p) => new Date(p.ts * 1000));
                const y = pts.map((p) => +p.c);
                const s20 = sma(y, 20);
                const s50 = sma(y, 50);

                setLineData({
                    labels: x,
                    datasets: [
                        {
                            label: `${ticker} Close`,
                            data: y,
                            borderColor: "rgba(59,130,246,1)",
                            backgroundColor: "rgba(59,130,246,0.08)",
                            tension: 0.2,
                            pointRadius: 0,
                        },
                        {
                            label: "SMA 20",
                            data: s20,
                            borderColor: "rgba(34,197,94,1)",
                            backgroundColor: "rgba(34,197,94,0)",
                            tension: 0.2,
                            pointRadius: 0,
                            borderWidth: 1.5,
                        },
                        {
                            label: "SMA 50",
                            data: s50,
                            borderColor: "rgba(239,68,68,1)",
                            backgroundColor: "rgba(239,68,68,0)",
                            tension: 0.2,
                            pointRadius: 0,
                            borderWidth: 1.5,
                        },
                    ],
                });

                setLineOptions({
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: true, labels: { color: "#e5e7eb" } },
                        title: {
                            display: true,
                            text: `${ticker} (${rangeNorm}/${intervalNorm})`,
                            color: "#93c5fd",
                        },
                        tooltip: { mode: "index", intersect: false },
                    },
                    scales: {
                        x: {
                            type: "time",
                            time: {
                                unit: (() => {
                                    const iv = intervalNorm;
                                    if (
                                        [
                                            "1m",
                                            "2m",
                                            "5m",
                                            "15m",
                                            "30m",
                                            "60m",
                                            "90m",
                                            "120m",
                                            "240m",
                                        ].includes(iv)
                                    ) {
                                        return iv === "60m" ||
                                            iv === "90m" ||
                                            (iv.endsWith("m") &&
                                                Number(
                                                    iv.replace("m", "")
                                                ) >= 60)
                                            ? "hour"
                                            : "minute";
                                    }
                                    if (iv === "1wk") return "week";
                                    if (iv === "1mo") return "month";
                                    return "day";
                                })(),
                            },
                            ticks: { color: "#9ca3af" },
                            grid: { color: "rgba(148,163,184,0.15)" },
                        },
                        y: {
                            ticks: { color: "#9ca3af" },
                            grid: { color: "rgba(148,163,184,0.15)" },
                        },
                    },
                });
            } catch (e) {
                if (axios.isCancel?.(e) || e?.name === "CanceledError") return;
                setErr("Failed to load chart data");
                console.error(e);
            } finally {
                setLoading(false);
            }
        }, 250);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (abortRef.current) abortRef.current.abort();
        };
    }, [ticker, range, interval]);

    // ---------- Prepare fallback candlestick (Chart.js-financial) ----------
    useEffect(() => {
        if (!candles?.length) return;
        const fin = candles.map((p) => ({
            x: new Date(p.time * 1000),
            o: p.open,
            h: p.high,
            l: p.low,
            c: p.close,
        }));
        setFallbackCandleData({
            datasets: [
                {
                    label: `${ticker} Candles`,
                    data: fin,
                    type: "candlestick",
                    borderColor: "#93c5fd",
                    color: {
                        up: "#22c55e",
                        down: "#ef4444",
                        unchanged: "#9ca3af",
                    },
                },
            ],
        });
        setFallbackCandleOptions({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: { mode: "index", intersect: false },
            },
            scales: {
                x: {
                    type: "time",
                    ticks: { color: "#9ca3af" },
                    grid: { color: "rgba(148,163,184,.15)" },
                },
                y: {
                    ticks: { color: "#9ca3af" },
                    grid: { color: "rgba(148,163,184,.15)" },
                },
            },
        });
    }, [candles, ticker]);

    // ---------- Mount Lightweight-Charts once per mode/range/interval ----------
    useEffect(() => {
        if (mode !== "candles") return;
        const el = chartContainerRef.current;
        if (!el) return;

        const cleanup = () => {
            try {
                resizeObsRef.current?.disconnect?.();
            } catch { }
            try {
                chartRef.current?.remove?.();
            } catch { }
            chartRef.current = null;
            candleSeriesRef.current = null;
            volumeSeriesRef.current = null;
        };

        (async () => {
            try {
                const mod = await import("lightweight-charts");
                const createChart = mod?.createChart;
                if (typeof createChart !== "function")
                    throw new Error("lightweight-charts not available");

                const chart = createChart(el, {
                    width: el.clientWidth,
                    height: 460,
                    layout: {
                        background: { color: "#0b1220" },
                        textColor: "#e5e7eb",
                    },
                    grid: {
                        vertLines: { color: "rgba(148,163,184,0.18)" },
                        horzLines: { color: "rgba(148,163,184,0.18)" },
                    },
                    rightPriceScale: {
                        borderColor: "rgba(148,163,184,0.25)",
                    },
                    timeScale: {
                        borderColor: "rgba(148,163,184,0.25)",
                        rightBarStaysOnScroll: true,
                        shiftVisibleRangeOnNewBar: true,
                        handleScroll: {
                            mouseWheel: true,
                            pressedMouseMove: true,
                        },
                        handleScale: {
                            axisPressedMouseMove: true,
                            pinch: true,
                            mouseWheel: true,
                        },
                    },
                    crosshair: { mode: 1 },
                });

                const candleSeries = chart.addCandlestickSeries({
                    upColor: "#22c55e",
                    downColor: "#ef4444",
                    borderUpColor: "#22c55e",
                    borderDownColor: "#ef4444",
                    wickUpColor: "#22c55e",
                    wickDownColor: "#ef4444",
                });
                candleSeries.setData(candles || []);

                const volumeSeries = chart.addHistogramSeries({
                    priceScaleId: "",
                    priceFormat: { type: "volume" },
                    base: 0,
                });
                volumeSeries.priceScale().applyOptions({
                    scaleMargins: { top: 0.85, bottom: 0 },
                });
                volumeSeries.setData(volumes || []);

                chart.timeScale().fitContent();

                chartRef.current = chart;
                candleSeriesRef.current = candleSeries;
                volumeSeriesRef.current = volumeSeries;

                if (resizeObsRef.current) resizeObsRef.current.disconnect();
                resizeObsRef.current = new ResizeObserver(() => {
                    if (!chartRef.current) return;
                    chartRef.current.applyOptions({ width: el.clientWidth });
                    // Do NOT fit on resize; preserve user scroll/zoom
                });
                resizeObsRef.current.observe(el);

                setUseFallback(false);
            } catch (e) {
                console.error(
                    "Candles: falling back to Chart.js financial:",
                    e
                );
                setUseFallback(true);
            }
        })();

        return () => cleanup();
    }, [mode, range, interval]); // remount only when these change

    // ---------- Update LWC series when new data arrives (no remount) ----------
    useEffect(() => {
        if (
            chartRef.current &&
            candleSeriesRef.current &&
            volumeSeriesRef.current
        ) {
            candleSeriesRef.current.setData(candles || []);
            volumeSeriesRef.current.setData(volumes || []);
        }
    }, [candles, volumes]);

    // ---------- UI ----------
    const chartKey = `${ticker}-${range}-${interval}-${mode}`;

    return (
        <div className="p-4 sm:p-6 text-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                        📈 <TickerWithName symbol={ticker} />
                    </h2>

                    {/* 🔥 Quick Trade Buttons */}
                    <div className="flex flex-wrap gap-3 mt-3">
                        {/* Trade Stock Button */}
                        <QuickTradeEquityButton ticker={ticker} />

                        {/* Trade Options Button */}
                        <button
                            className="px-3 py-1.5 rounded-md font-semibold text-xs sm:text-sm 
                                       bg-purple-700 hover:bg-purple-600 text-white shadow"
                            onClick={() =>
                                navigate(
                                    `/options-search?ticker=${encodeURIComponent(
                                        ticker
                                    )}`
                                )
                            }
                        >
                            Trade Options for {ticker}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mode toggle */}
            <div className="inline-flex rounded-xl overflow-hidden border border-gray-700 gap-3 mb-4">
                <button
                    onClick={() => setMode("line")}
                    className={`px-3 py-1.5 text-sm ${mode === "line" ? "bg-blue-600" : "bg-gray-800"
                        } hover:bg-blue-500`}
                >
                    Line
                </button>
                <button
                    onClick={() => setMode("candles")}
                    className={`px-3 py-1.5 text-sm ${mode === "candles" ? "bg-blue-600" : "bg-gray-800"
                        } hover:bg-blue-500`}
                >
                    Candles
                </button>
            </div>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="flex flex-wrap gap-2">
                    {RANGE_OPTIONS.map((r) => (
                        <button
                            key={r.key}
                            onClick={() => setRange(r.key)}
                            className={`px-3 py-1.5 rounded-lg text-sm ${range === r.key
                                    ? "bg-blue-600"
                                    : "bg-gray-800 hover:bg-gray-700"
                                }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <label className="text-sm text-gray-300">Interval</label>
                    <select
                        className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm"
                        value={interval}
                        onChange={(e) => setInterval(e.target.value)}
                    >
                        {INTERVAL_OPTIONS.map((iv) => (
                            <option key={iv} value={iv}>
                                {iv}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Charts */}
            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-3">
                {loading && <div className="text-gray-400">Loading…</div>}
                {err && <div className="text-red-400">{err}</div>}

                {/* Line mode */}
                {mode === "line" && lineData && lineOptions && (
                    <div className="h-[460px]">
                        <ReactChart
                            key={chartKey}
                            type="line"
                            data={{
                                labels: lineData.labels,
                                datasets: lineData.datasets.map((ds) => ({
                                    ...ds,
                                    data: lineData.labels.map(
                                        (_, i) => ds.data[i]
                                    ),
                                })),
                            }}
                            options={lineOptions}
                        />
                    </div>
                )}

                {/* Candles mode */}
                {mode === "candles" &&
                    (useFallback ? (
                        <div className="h-[460px]">
                            <ReactChart
                                key={chartKey}
                                type="candlestick"
                                data={fallbackCandleData}
                                options={fallbackCandleOptions}
                            />
                        </div>
                    ) : (
                        <div
                            ref={chartContainerRef}
                            className="w-full h-[460px]"
                            key={chartKey}
                        />
                    ))}
            </div>
        </div>
    );
};

export default ChartPage;
